import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CustomTabs } from '../../../components/CustomTabs';
import RiskyIdNumbersTab from './components/RiskyIdNumbersTab';
import BlacklistedMobileNumbersTab from './components/BlacklistedMobileNumbersTab';
import CompromisedEmployersTab from './components/CompromisedEmployersTab';
import ActionPoliciesTab from './components/ActionPoliciesTab';
import IdentityVerificationsTab from './components/IdentityVerificationsTab';

const SecurityTabs = {
  RISKY_ID: 'risky-id-numbers',
  BLACKLISTED_MOBILE: 'blacklisted-mobile-numbers',
  COMPROMISED_EMPLOYERS: 'compromised-employers',
  ACTION_POLICIES: 'action-policies',
  IDENTITY_VERIFICATIONS: 'identity-verifications',
} as const;

type SecurityTabsType = typeof SecurityTabs[keyof typeof SecurityTabs];

const SecurityPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<SecurityTabsType>(SecurityTabs.RISKY_ID);

  // Initialize tab from URL hash. Preserve the query string so deep-links
  // like /admin/security?employee_id=123#failed-attempts keep the
  // employee_id filter when we normalise the hash.
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && Object.values(SecurityTabs).includes(hash as SecurityTabsType)) {
      setActiveTab(hash as SecurityTabsType);
    } else {
      setActiveTab(SecurityTabs.RISKY_ID);
      history.replace(`${location.pathname}${location.search}#${SecurityTabs.RISKY_ID}`);
    }
  }, [location.hash, history, location.pathname, location.search]);

  const toggleTab = (tab: SecurityTabsType) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      // Preserve query string so per-tab filters seeded from `?employee_id=…`
      // survive a tab switch.
      history.push(`${location.pathname}${location.search}#${tab}`);
    }
  };

  return (
    <div className="min-h-screen bg-white p-md">
      {/* Negative -mx/-mt break out of the wrapper's p-md so the banner reaches the viewport edge. */}
      <div
        className="-mx-md -mt-md mb-md flex flex-wrap items-center justify-between gap-sm px-md py-lg"
        style={{ backgroundColor: 'rgba(237,244,251,0.5)' }}
      >
        <div>
          <h1 className="m-none font-heading text-2xl font-semibold text-greyscale-text-title">
            Security Controls
          </h1>
          <p className="m-none mt-xxs max-w-2xl text-sm text-greyscale-text-body">
            A central place for managing security safeguards and addressing potential risks
            across the platform.
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <CustomTabs
        tabs={[
          {
            id: SecurityTabs.RISKY_ID,
            label: 'Risky ID Numbers',
            tooltip: 'Employees with these ID numbers will have limited access to sensitive features.'
          },
          {
            id: SecurityTabs.BLACKLISTED_MOBILE,
            label: 'Blacklisted Mobile Numbers',
            tooltip: 'Users with these mobile numbers will not be able to access the product.'
          },
          {
            id: SecurityTabs.COMPROMISED_EMPLOYERS,
            label: 'Compromised Employers',
            tooltip: 'Existing employees of compromised employers will not be able to update their mobile numbers. Cold registration for new employees will remain unaffected.'
          },
          {
            id: SecurityTabs.ACTION_POLICIES,
            label: 'Action Policies',
            tooltip: 'Per-action verification policies. Global defaults plus per-employer overrides control which credentials a user must hold to perform each action.'
          },
          {
            id: SecurityTabs.IDENTITY_VERIFICATIONS,
            label: 'Identity Verifications',
            tooltip: 'ID-photo uploads with their fraud severity + signals. Default view shows high-and-above so reviewers see only the strongest signals; widen via the severity filter if needed.'
          }
        ]}
        activeTab={activeTab}
        onTabChange={(tabId) => toggleTab(tabId as SecurityTabsType)}
      />

      {/* Tab Content */}
      {activeTab === SecurityTabs.RISKY_ID && <RiskyIdNumbersTab />}
      {activeTab === SecurityTabs.BLACKLISTED_MOBILE && <BlacklistedMobileNumbersTab />}
      {activeTab === SecurityTabs.COMPROMISED_EMPLOYERS && <CompromisedEmployersTab />}
      {activeTab === SecurityTabs.ACTION_POLICIES && <ActionPoliciesTab />}
      {activeTab === SecurityTabs.IDENTITY_VERIFICATIONS && <IdentityVerificationsTab />}
    </div>
  );
};

export default SecurityPage;
